import React, { useCallback, useEffect, useRef, useState } from "react";
import BakeryDiningOutlinedIcon from "@mui/icons-material/BakeryDiningOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import OperationsPageHeader from "../home/top_bar_components/OperationsPageHeader";
import SubTabs from "../home/top_bar_components/SubTabs";
import PrepPlanDisplay, { parseISODate, shiftISODate } from "./PrepPlanDisplay";

const hasItemName = item => typeof item.name === "string" && item.name.trim().length > 0;

function DayButton({ onClick, label, disabled, children }) {
  return <button type="button" onClick={onClick} aria-label={label} disabled={disabled}
    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:border-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700 disabled:opacity-50">
    {children}
  </button>;
}

// Current stock is the internal Toast preview; Prep plan is shown to managers
// once their company has prep items.
export default function InventoryDisplay({ inventory, prepPlan, onPrepDateChange, onPrepDirtyChange, internal, ...props }) {
  const { snapshot, loading, error, reason, refresh } = inventory;
  const showPrep = Boolean(prepPlan?.available);
  const showStock = Boolean(internal) || !showPrep;
  const [tab, setTab] = useState("stock");
  // Leaving the Prep plan or changing location drops unsaved waste entries.
  const prepDirtyRef = useRef(false);
  const handlePrepDirty = useCallback((dirty) => {
    prepDirtyRef.current = dirty;
    onPrepDirtyChange?.(dirty);
  }, [onPrepDirtyChange]);
  const okToDiscard = () => !prepDirtyRef.current || window.confirm("Discard unsaved changes?");
  const activeTab = showPrep && (tab === "prep" || !showStock) ? "prep" : "stock";
  const prepDate = prepPlan?.plan?.target_date;
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [showUnnamed, setShowUnnamed] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => { setFilter("all"); setQuery(""); setShowUnnamed(false); }, [props.homeStore]);

  const items = snapshot?.items || [];
  const unnamedCount = items.filter(item => !hasItemName(item)).length;
  const noNamedItems = items.length > 0 && unnamedCount === items.length && !showUnnamed;
  const out = items.filter(item => item.status === "OUT_OF_STOCK").length;
  const low = items.filter(item => item.running_low).length;
  const tracked = items.filter(item => item.status === "QUANTITY").length;
  const stale = snapshot && (error || now - Date.parse(snapshot.checked_at) > 60000);
  const visible = items.filter(item =>
    (showUnnamed || hasItemName(item)) &&
    (filter === "all" || (filter === "out" && item.status === "OUT_OF_STOCK") ||
      (filter === "low" && item.running_low) || (filter === "tracked" && item.status === "QUANTITY")) &&
    `${item.name || ""} ${item.id} ${item.kind}`.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white pt-8 pb-12 text-slate-900">
      <OperationsPageHeader {...props} change_home_store={(...args) => { if (okToDiscard()) return props.change_home_store?.(...args); return undefined; }} title="Inventory" titleBadge={activeTab === "stock" &&
        <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">Internal preview</span>
      }>
        <SubTabs className="mt-5" fullWidth
          tabs={[
            showStock && { key: "stock", label: "Current stock", icon: Inventory2OutlinedIcon },
            showPrep && { key: "prep", label: "Prep plan", icon: BakeryDiningOutlinedIcon },
          ].filter(Boolean)}
          activeTab={activeTab} onChange={(next) => { if (okToDiscard()) setTab(next); }}
          rightSlot={activeTab === "prep" && prepDate && <>
            {/* Steps from the day on screen, so wait for a requested day to load first. */}
            <DayButton onClick={() => onPrepDateChange(shiftISODate(prepDate, -1))} label="Previous day" disabled={prepPlan.loading}>
              <ChevronLeftIcon style={{ fontSize: "20px" }} />
            </DayButton>
            <span className="w-56 whitespace-nowrap text-center text-sm font-semibold tabular-nums text-slate-800">
              {parseISODate(prepDate).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </span>
            <DayButton onClick={() => onPrepDateChange(shiftISODate(prepDate, 1))} label="Next day" disabled={prepPlan.loading}>
              <ChevronRightIcon style={{ fontSize: "20px" }} />
            </DayButton>
          </>} />
      </OperationsPageHeader>
      <div className="mx-10 mt-7 space-y-6">
        {activeTab === "prep" ? <PrepPlanDisplay prepPlan={prepPlan} internal={internal} onDirtyChange={handlePrepDirty} homeStore={props.homeStore} /> : <>
        <div className="flex items-center justify-between gap-4 sm:flex-col sm:items-start">
          <div>
            <h2 className="text-lg font-semibold">A clearer view of what’s available</h2>
            <p className="mt-1 text-sm text-slate-500">86’d items and quantities reported by Toast at this location.</p>
            <p className="mt-2 text-xs text-slate-500" aria-live="polite">
              {snapshot ? <>Last checked {new Date(snapshot.checked_at).toLocaleString()} · {stale ? "Refresh recommended" : "Manual refresh"}</> : "Current stock is checked on opening and when you refresh."}
            </p>
          </div>
          <button type="button" onClick={refresh} disabled={loading}
            className="flex shrink-0 items-center gap-2 rounded-lg bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-700 disabled:opacity-50">
            <RefreshIcon sx={{ fontSize: 18 }} />{loading ? "Checking Toast…" : "Refresh stock"}
          </button>
        </div>

        {error && <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {error}{snapshot && " Showing the last successful snapshot; these counts may have changed."}
        </div>}
        {loading && !snapshot && <p role="status" className="py-12 text-center text-sm text-slate-500">Checking inventory access and current stock…</p>}
        {!loading && !snapshot && !error && <div className="rounded-xl border border-slate-200 px-6 py-12 text-center">
          <Inventory2OutlinedIcon className="text-slate-400" sx={{ fontSize: 32 }} />
          <h3 className="mt-3 font-semibold">Inventory isn’t available at this location</h3>
          <p className="mt-2 text-sm text-slate-500">{reason === "stock_access_required"
            ? "Toast hasn’t granted stock access for this location. Once access is enabled, refresh to check again."
            : "Select a location with a connected Toast account and inventory API access."}</p>
        </div>}

        {snapshot && <>
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-1" aria-label="Stock summary">
            {[
              { label: "86’d", count: out, detail: "Marked out of stock in Toast", color: "text-rose-700" },
              { label: "Running low", count: low, detail: `More than 0, up to ${snapshot.low_stock_threshold} remaining`, color: "text-amber-700" },
              { label: "Quantity tracked", count: tracked, detail: "Items with a stock quantity status", color: "text-violet-700" },
            ].map(card => <div key={card.label} className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-medium text-slate-600">{card.label}</p>
              <p className={`mt-3 text-3xl font-semibold ${card.color}`}>{card.count}</p>
              <p className="mt-2 text-xs text-slate-500">{card.detail}</p>
            </div>)}
          </div>

          <section className="overflow-hidden rounded-xl border border-slate-200" aria-label="Current inventory">
            <div className="flex items-end gap-4 border-b border-slate-200 p-5 sm:flex-col sm:items-stretch">
              <div className="w-56 sm:w-full">
                <label id="inventory-status-label" htmlFor="inventory-status" className="mb-1.5 block text-xs font-semibold text-slate-600">Stock status</label>
                <RightworkSelect id="inventory-status" value={filter} onChange={event => setFilter(event.target.value)} options={[
                  { value: "all", label: `All statuses (${items.length})` },
                  { value: "out", label: `86’d (${out})` },
                  { value: "low", label: `Running low (${low})` },
                  { value: "tracked", label: `Quantity tracked (${tracked})` },
                ]} />
              </div>
              <div className="relative max-w-md flex-1 sm:max-w-none">
                <label htmlFor="inventory-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search items</label>
                <SearchIcon className="absolute left-3 bottom-2.5 text-slate-400" sx={{ fontSize: 20 }} />
                <input id="inventory-search" type="search" value={query} onChange={event => setQuery(event.target.value)}
                  placeholder="Search by name or Toast ID" className="h-[42px] w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-violet-700 focus:ring-1 focus:ring-violet-700" />
              </div>
            </div>
            {unnamedCount > 0 && <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-5 py-3 text-xs text-slate-600">
              <p>{unnamedCount} unnamed {unnamedCount === 1 ? "item" : "items"} {showUnnamed ? "shown by Toast ID" : "hidden from the list"}. Stock totals include {unnamedCount === 1 ? "this item" : "these items"}.</p>
              <button type="button" aria-pressed={showUnnamed} onClick={() => setShowUnnamed(value => !value)}
                className="shrink-0 rounded px-1 py-1 font-semibold text-violet-700 hover:text-violet-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700">
                {showUnnamed ? "Hide unnamed items" : "Show unnamed items"}
              </button>
            </div>}
            <p className="hidden border-b border-slate-200 px-5 py-2 text-xs text-slate-500 sm:block">Scroll horizontally to view all columns.</p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[480px] text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-500"><tr>
                  <th className="px-5 py-3" scope="col">Item</th><th className="px-5 py-3" scope="col">Remaining</th><th className="px-5 py-3" scope="col">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">{visible.map(item => <tr key={item.id}>
                  <td className="px-5 py-4"><div className="font-medium">{hasItemName(item) ? item.name : "Unnamed Toast item"}</div>
                    <div className="mt-1 break-all text-xs text-slate-400">{hasItemName(item) ? item.kind : `Toast ID: ${item.id}`}</div></td>
                  <td className="whitespace-nowrap px-5 py-4 font-medium tabular-nums">{item.quantity == null ? <span className="font-normal text-slate-400">— No count</span> : item.quantity.toLocaleString(undefined, { maximumFractionDigits: 6 })}</td>
                  <td className="whitespace-nowrap px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${item.status === "OUT_OF_STOCK" ? "bg-rose-50 text-rose-700" : item.running_low ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                    {item.status === "OUT_OF_STOCK" ? "86’d" : item.running_low ? "Running low" : "Quantity tracked"}
                  </span></td>
                </tr>)}</tbody>
              </table>
              {visible.length === 0 && <div className="px-5 py-12 text-center">
                <p className="font-medium">{items.length === 0 ? "No 86’d or quantity-tracked items returned" : noNamedItems ? "No named items available" : "No items match these filters"}</p>
                <p className="mt-2 text-sm text-slate-500">{items.length === 0 ? "Toast returned an empty stock list at the last check." : noNamedItems ? "Toast returned stock data, but no item names could be matched. Show unnamed items to inspect the stock by Toast ID." : "Try another status or search term."}</p>
              </div>}
            </div>
            <div className="border-t border-slate-200 px-5 py-3 text-xs text-slate-500">{visible.length} of {items.length} returned items{!showUnnamed && unnamedCount > 0 && ` · ${unnamedCount} unnamed hidden`}</div>
          </section>
          <p className="max-w-3xl text-xs leading-5 text-slate-500">Toast returns out-of-stock and quantity-tracked items here; uncounted in-stock items are omitted. Running low is a Rightwork rule of more than 0 and up to {snapshot.low_stock_threshold} remaining. Refreshes within 30 seconds may reuse the last check. Changes between checks aren’t captured, and notifications aren’t enabled in this preview.</p>
        </>}
        </>}
      </div>
    </div>
  );
}
